import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Button } from './ui/button';
import { useCurrency } from '../contexts/CurrencyContext';
import { Loader2, Search } from 'lucide-react';
import { trackCheckoutStarted } from '../utils/analytics';
import { PRICING_CONFIG } from '../config/pricing';
import { INDUSTRIES_LIST } from '../data/industries-list';

const CheckoutButton = ({ plan, className, children, variant = 'default', industry = null, ...props }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showIndustryModal, setShowIndustryModal] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState(industry || '');
  const [searchTerm, setSearchTerm] = useState('');
  const { currency, formatPrice } = useCurrency();

  const filteredIndustries = INDUSTRIES_LIST.filter(industry =>
    industry.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleButtonClick = () => {
    // If industry is pre-selected (from offer page), go directly to checkout
    if (industry) {
      handleCheckout();
    } else {
      // Otherwise show industry selector modal
      setShowIndustryModal(true);
      setError(null);
    }
  };

  const handleCheckout = async () => {
    if (!selectedIndustry) {
      setError('Please select your industry');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Track checkout initiation
      const planConfig = PRICING_CONFIG[currency][plan];
      trackCheckoutStarted({
        plan: plan,
        currency: currency,
        value: planConfig?.amount || 0,
        industry: selectedIndustry
      });
      
      // Get customer info (will be collected by Stripe Checkout)
      const customerInfo = {
        currency,
        plan,
        industry: selectedIndustry,
        email: '', // Will be collected by Stripe Checkout
        name: '',  // Will be collected by Stripe Checkout
        company: '', // Will be collected by Stripe Checkout
        _ts: Date.now() // Add timestamp to make each request unique
      };

      console.log('🔍 Initiating checkout with:', customerInfo);

      // Call your backend to create checkout session
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      console.log('🔧 DEBUG - process.env.REACT_APP_BACKEND_URL:', process.env.REACT_APP_BACKEND_URL);
      console.log('🔧 DEBUG - backendUrl value:', backendUrl);
      console.log('🔧 REBUILD TEST:', Date.now());
      
      if (!backendUrl) {
        throw new Error('REACT_APP_BACKEND_URL environment variable is not configured');
      }
      
      // Use v2 endpoint to bypass cached errors on old endpoint
      const cacheBuster = Date.now();
      const apiUrl = `${backendUrl}/api/stripe/checkout-v2?v=${cacheBuster}`;
      
      console.log('📡 API URL:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify(customerInfo),
      });

      console.log('📥 Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Checkout session error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(errorData.error || `Failed to process checkout request (${response.status})`);
      }

      const { url } = await response.json();
      console.log('✅ Stripe URL received:', url);
      
      // Redirect to Stripe Checkout
      window.location.href = url;
      
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message);
      setIsLoading(false);
    }
  };

  return (
    <>
      <div>
        <Button 
          onClick={handleButtonClick}
          disabled={isLoading}
          className={className}
          variant={variant}
          {...props}
        >
          {children || `Start 14-Day Trial for ${formatPrice(5)}`}
        </Button>
        
        {error && !showIndustryModal && (
          <div className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
            {error}
          </div>
        )}
      </div>

      {/* Industry Selection Modal - Rendered as Portal */}
      {showIndustryModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] p-6 text-white">
              <h2 className="text-2xl font-bold mb-2">Select Your Industry</h2>
              <p className="text-white/90">We'll set up your account with the perfect template for your business</p>
            </div>
            
            <div className="p-6">
              {/* Search Box */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search for your industry..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#FF5A3D] focus:outline-none"
                />
              </div>

              {/* Industry List */}
              <div className="max-h-96 overflow-y-auto border-2 border-gray-100 rounded-lg">
                {filteredIndustries.map((industry) => (
                  <button
                    key={industry.value}
                    onClick={() => setSelectedIndustry(industry.value)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors ${
                      selectedIndustry === industry.value ? 'bg-[#FF5A3D]/10 border-l-4 border-l-[#FF5A3D]' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`font-medium ${selectedIndustry === industry.value ? 'text-[#FF5A3D]' : 'text-gray-700'}`}>
                        {industry.label}
                      </span>
                      {selectedIndustry === industry.value && (
                        <svg className="w-5 h-5 text-[#FF5A3D]" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              {error && (
                <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
                  {error}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => {
                    setShowIndustryModal(false);
                    setSelectedIndustry('');
                    setSearchTerm('');
                  }}
                  variant="outline"
                  className="flex-1"
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCheckout}
                  disabled={!selectedIndustry || isLoading}
                  className="flex-1 bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    `Continue to Checkout`
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default CheckoutButton;